import React, { useState, useCallback } from "react";
import { 
  SuccessMessage,
  FailMessage,
  Loading,
  VendorSelect,
  CompanySelect,
  FormatValueToDecimal,
  FormatDateToMDY,
  DateSelect
} from "../../common/APICommon";
import { apiUrl } from "../../utils/Configuration";
import axios from 'axios';
import { PDFDocument, rgb, StandardFonts } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import NoAccess from "../NoAccess";
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export default function VendorLedger() {
//#region Others
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [failMessage, setFailMessage] = useState("");
//#endregion






//#region Vendor 
  const [vendor, setVendor] = useState(null);
  const [company, setCompany] = useState(null);
  const today = new Date().toISOString().split("T")[0];
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo] = useState(today);
  const [showVendorSelect, setShowVendorSelect] = useState(false);

  const openVendorSelect = () => {
    setShowVendorSelect(true)
  }
  const closeVendorSelect = () => {
    setShowVendorSelect(false)
  }
//#endregion








//#region Generate
  const fetchReportData = useCallback(async (_data) => {
    const body = {
      vendorId: _data.vendorId,
      dateFrom: dateFrom,
      dateTo: dateTo,
    }

    try {
      const response = await axios.post(`${apiUrl}/api/Report/report-data/vendor-ledger`, body, {
        headers: { 'Content-Type': 'application/json'}
      })
        
      return (response.data)
    } catch (error) {
      console.error('Error fetching data:', error);
      return null
    }
  }, [dateFrom, dateTo]);

  const generateHandleClick = async () => {
    if (!vendor || !company || !dateFrom || !dateTo) {
      setFailMessage("Please fill up required fields");
      return;
    }

    setLoading(true);
    const _data = await fetchReportData(vendor);

    try {
      const pdfUrl = `${window.location.origin}/docs/Vendor_Ledger.pdf`;
      const response = await fetch(pdfUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);

      pdfDoc.registerFontkit(fontkit);
      const fontBytes = await (await fetch("/calibri.ttf")).arrayBuffer();
      const calibriFont = await pdfDoc.embedFont(fontBytes);
      const boldBytes = await (await fetch("/calibrib.ttf")).arrayBuffer();
      const calibriBold = await pdfDoc.embedFont(boldBytes);
      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);

      const wrapText = (text, maxWidth, font, fontSize) => {
        if (!text) return [""];
        const words = text.split(/(?<=-)|\s+/);
        const lines = [];
        let currentLine = '';
        for (const word of words) {
          const testLine = currentLine ? `${currentLine}${word}` : word;
          if (font.widthOfTextAtSize(testLine, fontSize) <= maxWidth) {
            currentLine = testLine;
          } else {
            if (currentLine) lines.push(currentLine);
            currentLine = word;
          }
        }
        if (currentLine) lines.push(currentLine);
        return lines;
      };

      // --- REUSABLE HEADER FUNCTION ---
      const drawHeader = (targetPage) => {
        targetPage.drawText(company.name?.toString() ?? "", { x: 35, y: 580, size: 16, font: calibriBold });
        targetPage.drawText(company.address?.toString() ?? "", { x: 35, y: 570, size: 7, font: calibriFont });
        targetPage.drawText(company.tin?.toString() ?? "", { x: 35, y: 560, size: 7, font: calibriFont });
        targetPage.drawText(vendor?.name1?.toString() ?? "", { x: 35, y: 540, size: 7, font: calibriFont });
        targetPage.drawText(`${FormatDateToMDY(dateFrom)} - ${FormatDateToMDY(dateTo)}`, { x: 93, y: 515.5, size: 7, font: calibriFont });
        targetPage.drawText(FormatDateToMDY(today), { x: 82, y: 505.5, size: 7, font: calibriFont });
        
        // Draw Table Column Headers if your template doesn't have them on every page
        // targetPage.drawText("Date", { x: 37.5, y: 460, size: 7, font: calibriBold }); 
      };

      let currentPage = pdfDoc.getPages()[0];
      drawHeader(currentPage);

      let yPosition = 440;
      const bottomThreshold = 60; // When to trigger a new page
      const colWidthDebitDataDocNoLines = 25;
      const colWidthCreditDataDocNoLines = 50;
      const colWidthCreditDataCheckbookNumberLines = 50;
      const lineHeight = 10;

      let begBalance = 0;
      let purchase = 0;
      let payment = 0;
      let purchaseReturn = 0;
      let upappliedPayment = 0;
      let endingBalance = 0;

      for (let index = 0; index < _data.length; index++) {
        // --- NEW PAGE CHECK ---
        if (yPosition < bottomThreshold) {
          currentPage = pdfDoc.addPage([
            currentPage.getWidth(),
            currentPage.getHeight()
          ]);
          drawHeader(currentPage);
          yPosition = 480;
        }

        const rowStartY = yPosition;

        const debitDataList = _data[index]?.details?.debit ?? [];
        const creditDataList = _data[index]?.details?.credit ?? [];

        // Use the first credit date for the days-difference calculation.
        const creditDateObj = creditDataList.length > 0 && creditDataList[0].date
          ? new Date(creditDataList[0].date)
          : null;

        // --- DEBIT COLUMN ---
        let debitYCursor = rowStartY;

        for (let detail = 0; detail < debitDataList.length; detail++) {
          const debitData = debitDataList[detail];

          if (debitYCursor < bottomThreshold) {
            currentPage = pdfDoc.addPage([
              currentPage.getWidth(),
              currentPage.getHeight()
            ]);
            drawHeader(currentPage);
            debitYCursor = 480;
          }

          currentPage.drawText(
            `${FormatDateToMDY(debitData.date)}`,
            { x: 37.5, y: debitYCursor, size: 7, font: calibriFont }
          );

          currentPage.drawText(
            "REC",
            { x: 85, y: debitYCursor, size: 7, font: calibriFont }
          );

          const debitLines = wrapText(
            debitData.documentNumber?.toString() ?? "",
            colWidthDebitDataDocNoLines,
            calibriFont,
            7
          );

          for (let j = 0; j < debitLines.length; j++) {
            currentPage.drawText(debitLines[j], {
              x: 118,
              y: debitYCursor - (j * lineHeight),
              size: 7,
              font: calibriFont
            });
          }

          currentPage.drawText(
            debitData.referenceNumber?.toString() ?? "-",
            { x: 170, y: debitYCursor, size: 7, font: calibriFont }
          );

          currentPage.drawText(
            FormatValueToDecimal(debitData.amount)?.toString() ?? "",
            { x: 218, y: debitYCursor, size: 7, font: calibriFont }
          );

          currentPage.drawText(
            FormatValueToDecimal(debitData.balance)?.toString() ?? "",
            { x: 278, y: debitYCursor, size: 7, font: calibriFont }
          );

          let daysDiff = "-";

          if (debitData.date && creditDateObj) {
            const debitDateObj = new Date(debitData.date);
            const timeDiff = creditDateObj.getTime() - debitDateObj.getTime();

            daysDiff = Math.max(
              1,
              Math.ceil(timeDiff / (1000 * 3600 * 24))
            ).toString();
          }

          currentPage.drawText(daysDiff, {
            x: 465,
            y: debitYCursor,
            size: 7,
            font: calibriFont
          });

          const itemHeight = Math.max(
            15,
            (debitLines.length * lineHeight) + 2
          );

          debitYCursor -= itemHeight;

          begBalance += debitData.balance ?? 0;
          purchase += debitData.amount ?? 0;
        }

        // --- CREDIT COLUMN ---
        let creditYCursor = rowStartY;

        for (let creditIndex = 0; creditIndex < creditDataList.length; creditIndex++) {
          const creditData = creditDataList[creditIndex];

          if (creditYCursor < bottomThreshold) {
            currentPage = pdfDoc.addPage([
              currentPage.getWidth(),
              currentPage.getHeight()
            ]);
            drawHeader(currentPage);
            creditYCursor = 480;
          }

          currentPage.drawText(
            `${FormatDateToMDY(creditData.date)}`,
            { x: 336, y: creditYCursor, size: 7, font: calibriFont }
          );

          currentPage.drawText(
            "APP",
            { x: 380, y: creditYCursor, size: 7, font: calibriFont }
          );

          const creditLines = wrapText(
            creditData.documentNumber?.toString() ?? "",
            colWidthCreditDataDocNoLines,
            calibriFont,
            7
          );

          for (let j = 0; j < creditLines.length; j++) {
            currentPage.drawText(creditLines[j], {
              x: 411,
              y: creditYCursor - (j * lineHeight),
              size: 7,
              font: calibriFont
            });
          }

          currentPage.drawText(
            creditData.bankName?.toString() ?? "-",
            { x: 557, y: creditYCursor, size: 7, font: calibriFont }
          );

          const checkLines = wrapText(
            creditData.checkbookNumber?.toString() ?? "",
            colWidthCreditDataCheckbookNumberLines,
            calibriFont,
            7
          );

          for (let j = 0; j < checkLines.length; j++) {
            currentPage.drawText(checkLines[j], {
              x: 593,
              y: creditYCursor - (j * lineHeight),
              size: 7,
              font: calibriFont
            });
          }

          currentPage.drawText(
            `${FormatDateToMDY(creditData.checkbookDate) ?? ""}`,
            { x: 653, y: creditYCursor, size: 7, font: calibriFont }
          );

          currentPage.drawText(
            FormatValueToDecimal(creditData.amount)?.toString() ?? "",
            { x: 706, y: creditYCursor, size: 7, font: calibriFont }
          );

          const creditMaxLines = Math.max(
            creditLines.length,
            checkLines.length
          );

          creditYCursor -= Math.max(
            15,
            (creditMaxLines * lineHeight) + 2
          );

          payment += creditData.amount ?? 0;
        }

        // --- RETURNS COLUMN (UNDER PAYMENTS) ---
        let returnYCursor = creditYCursor;

        const returnDataList = _data[index]?.details?.returns ?? [];

        for (let returnIndex = 0; returnIndex < returnDataList.length; returnIndex++) {
          const returnData = returnDataList[returnIndex];

          // Check if a new page is needed
          if (returnYCursor < bottomThreshold) {
            currentPage = pdfDoc.addPage([
              currentPage.getWidth(),
              currentPage.getHeight()
            ]);

            drawHeader(currentPage);
            returnYCursor = 480;
          }

          // Return date
          currentPage.drawText(
            `${FormatDateToMDY(returnData.date)}`,
            { x: 336, y: returnYCursor, size: 7, font: calibriFont }
          );

          // Transaction type
          currentPage.drawText(
            "PUR RET",
            { x: 380, y: returnYCursor, size: 7, font: calibriFont }
          );

          // Return document number
          const returnLines = wrapText(
            returnData.documentNumber?.toString() ?? "",
            colWidthCreditDataDocNoLines,
            calibriFont,
            7
          );

          for (let j = 0; j < returnLines.length; j++) {
            currentPage.drawText(returnLines[j], {
              x: 411,
              y: returnYCursor - (j * lineHeight),
              size: 7,
              font: calibriFont
            });
          }

          // Return amount
          currentPage.drawText(
            FormatValueToDecimal(returnData.amount ?? 0)?.toString() ?? "",
            { x: 706, y: returnYCursor, size: 7, font: calibriFont }
          );

          // Move down based on the number of wrapped lines
          const itemHeight = Math.max(
            15,
            (returnLines.length * lineHeight) + 2
          );

          returnYCursor -= itemHeight;
          purchaseReturn += Number(returnData.amount ?? 0);
        }

        // --- FINISH ROW ---
        yPosition = Math.min(
          debitYCursor,
          creditYCursor,
          returnYCursor
        );

        currentPage.drawText(
          "______________________________________________________________________________________________________________________________________________________________________________________________________________",
          {
            x: 35,
            y: yPosition + 5,
            size: 7,
            font: calibriFont
          }
        );

        yPosition -= 10;
      }

      // Beg Bal and Footer Info
      if (yPosition < 160) { 
        currentPage = pdfDoc.addPage([currentPage.getWidth(), currentPage.getHeight()]);
        yPosition = 550; // Reset to top of new page
        drawHeader(currentPage); // Draw header on the new page too
      }

      // Outstanding balance after payments and sales returns
      let finalCollection = payment - purchaseReturn
      endingBalance = finalCollection - begBalance;

      // Unapplied collection should not include sales returns
      upappliedPayment = Math.max(0, payment - purchase);

      currentPage.drawText("TOTAL", { x: 35, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(begBalance)?.toString() ?? "", { x: 218, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(purchase)?.toString() ?? "", { x: 278, y: yPosition, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(payment)?.toString() ?? "", { x: 706, y: yPosition, size: 7, font: calibriBold });

      currentPage.drawText("NET AMOUNT", { x: 336, y: yPosition - 10, size: 7, font: calibriBold });
      currentPage.drawText(FormatValueToDecimal(endingBalance)?.toString() ?? "0.00", { x: 706, y: yPosition - 10, size: 7, font: calibriBold });

      let footerY = yPosition - 40;

      currentPage.drawText("*** Vendor is member of Payment Group", { x: 35, y: footerY, size: 7, font: helveticaFont });

      currentPage.drawText("Beg. Bal:", { x: 35, y: footerY - 20, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(begBalance)?.toString() ?? "", { x: 170, y: footerY - 20, size: 7, font: helveticaFont });
      
      currentPage.drawText("Purchases:", { x: 35, y: footerY - 30, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(purchase)?.toString() ?? "", { x: 170, y: footerY - 30, size: 7, font: helveticaFont });

      currentPage.drawText("Other Payables:", { x: 35, y: footerY - 40, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 40, size: 7, font: helveticaFont });

      currentPage.drawText("Less:", { x: 35, y: footerY - 50, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 50, size: 7, font: helveticaFont });

      currentPage.drawText("Cash Purchase:", { x: 35, y: footerY - 60, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 60, size: 7, font: helveticaFont });

      currentPage.drawText("Purchase Return:", { x: 35, y: footerY - 70, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(purchaseReturn)?.toString() ?? "0.00", { x: 170, y: footerY - 70, size: 7, font: helveticaFont });

      currentPage.drawText("EWT:", { x: 35, y: footerY - 80, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 80, size: 7, font: helveticaFont });

      currentPage.drawText("EVAT:", { x: 35, y: footerY - 90, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 90, size: 7, font: helveticaFont });

      currentPage.drawText("Payment:", { x: 35, y: footerY - 100, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(payment)?.toString() ?? "", { x: 170, y: footerY - 100, size: 7, font: helveticaFont });

      currentPage.drawText("Unapplied Payments:", { x: 35, y: footerY - 110, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(upappliedPayment)?.toString() ?? "", { x: 170, y: footerY - 110, size: 7, font: helveticaFont });

      currentPage.drawText("Ending Bal.:", { x: 35, y: footerY - 120, size: 7, font: helveticaFont });
      currentPage.drawText(FormatValueToDecimal(endingBalance)?.toString(), { x: 170, y: footerY - 120, size: 7, font: helveticaFont });

      currentPage.drawText("Direct Payment:", { x: 35, y: footerY - 130, size: 7, font: helveticaFont });
      currentPage.drawText("0.00", { x: 170, y: footerY - 130, size: 7, font: helveticaFont });

      // --- ADD PAGE NUMBERS ---
      const pages = pdfDoc.getPages();
      for (let i = 0; i < pages.length; i++) {
        pages[i].drawText(`Page ${i + 1} of ${pages.length}`, {
          x: pages[i].getWidth() - 100,
          y: 20,
          size: 8,
          font: calibriFont,
          color: rgb(0, 0, 0),
        });
      }

      const modifiedPdfBytes = await pdfDoc.save();
      const blob = new Blob([modifiedPdfBytes], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `Vendor_Ledger.pdf`;
      a.click();
    } catch (error) {
      console.error("Error:", error);
      setFailMessage("Error processing PDF");
    }
    setLoading(false);
  };
//#endregion






//#region Access Template
  const accountData = JSON.parse(localStorage.getItem("accountData"));
  
  if (!accountData?.accessTemplate?.hasVendorLedger) {
    return <NoAccess />;
  }
//#endregion





//#region UI
  return (
    <>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8 gap-4">
          <div>
            <h2 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <HistoryEduIcon fontSize="large" className="text-theme-color" />
              {`Vendor Ledger`}
            </h2>
            <p className="text-gray-500 mt-1">Record of all transactions related to each vendor.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="max-w-2xl">
            <label className="block text-sm font-semibold mb-1 text-gray-700">
              Vendor <span className="text-red-500">*</span>
            </label>
            <div className="flex flex-row items-center">
              <input
                type="text"
                placeholder="Select Vendor"
                className="w-full border border-gray-300 text-sm rounded-l-lg px-3 py-2 text-gray-800 focus:outline-none focus:ring-2 focus:ring-theme-color focus:border-theme-color transition"
                value={`${vendor ? `${vendor?.vendorId} - ${vendor?.name1}` : ""}`}
                readOnly
              />
              {!vendor ?
                <button className="bg-theme-color hover:bg-theme-color-h p-[5px] rounded-r-lg" onClick={openVendorSelect}>
                  <PersonSearchIcon className="text-white"/>
                </button> 
                :
                <button className="bg-red-400 hover:bg-red-500 p-[5px] rounded-r-lg" onClick={()=>setVendor(null)}>
                  <RemoveCircleIcon className="text-white"/>
                </button>
              }
            </div>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Company <span className="text-red-500">*</span>
            </label>
            <CompanySelect data={company} setData={setCompany} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date From <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateFrom} setData={setDateFrom} isEditable={true}/>
          </div>

          <div className="max-w-2xl">
            <label className="block text-sm font-medium mb-1 text-gray-700 ">
              Date To <span className="text-red-500">*</span>
            </label>
            <DateSelect data={dateTo} setData={setDateTo} isEditable={true}/>
          </div>
        </div>

        <button onClick={generateHandleClick} className="mt-10 flex items-center gap-2 bg-theme-color text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-theme-color-h transition shadow-md">
          <RestartAltIcon fontSize="small" /> Generate
        </button>
      </div>

      <VendorSelect 
        visible={showVendorSelect} 
        onClose={closeVendorSelect}
        modalName={"Select Vendor"}
        data={vendor} setData={setVendor}
      />

      <div className="fixed top-5 right-5 z-50 flex flex-col space-y-2">
        <SuccessMessage message={successMessage} onClose={() => setSuccessMessage("")} />
        <FailMessage message={failMessage} onClose={() => setFailMessage("")} />
      </div>

      { loading && <Loading />}
    </>
  );
//#endregion
}